import { ChangeEvent, ReactNode, useCallback } from 'react';
import { rgba } from 'polished';
import styled from 'styled-components';

import WarningIcon from '#/assets/WarningIcon';

import { cFA9400, cFFFFFF } from '#/colors';

import Toggle from '#/components/common/Toggle';
import Field from '#/components/shared/Field';
import Button from '#/components/shared/Button';
import * as Alert from '#/components/common/Alert';
import toast from '#/components/common/toast';

import {
  useUsernameFormView,
  useUsernameFormActions,
} from '#/features/account/username-form-context';
import { logException } from '#/features/logging/logging';
import { useTranslation } from '#/features/localization/utils';

import { Result } from '#/utils/types';

interface FormProps {
  readonly children: ReactNode;
  readonly onSubmitComplete?: (result: Result<void>) => void;
}

export function Form(props: FormProps) {
  const { onSubmitComplete } = props;

  const formActions = useUsernameFormActions();
  const { t } = useTranslation();

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      formActions
        .submit()
        .then((result) => {
          if (result.ok) {
            toast.success(t('Your username preferences were updated!'));
          }
          onSubmitComplete?.(result);
        })
        .catch((cause: unknown) => {
          const message = t('Unexpected error updating username preferences');
          const error = new Error(message, { cause });
          toast.error(message);
          logException(error);
          onSubmitComplete?.({ ok: false, reason: message, error });
        });
    },
    [formActions, onSubmitComplete, t],
  );

  return (
    <Grid as="form" onSubmit={handleSubmit}>
      {props.children}
    </Grid>
  );
}

export function Fields() {
  const formView = useUsernameFormView();
  return (
    <Grid>
      <InputUsername />
      <ToggleHideUsername />
      {formView.touched.username && formView.errors.username.length > 0 && (
        <Alert.Warning>{formView.errors.username}</Alert.Warning>
      )}
      {formView.error.length > 0 && (
        <Alert.Warning>{formView.error}</Alert.Warning>
      )}
    </Grid>
  );
}

const Grid = styled.div`
  display: grid;
  gap: 16px;
`;

export function InputUsername() {
  const formView = useUsernameFormView();
  const formActions = useUsernameFormActions();
  const { t } = useTranslation();

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      formActions.setUsername(event.target.value);
    },
    [formActions],
  );

  return (
    <Field label={t('Username')} labelId="username-form-username-input">
      <Input
        id="username-form-username-input"
        type="text"
        value={formView.values.username}
        onChange={handleChange}
        disabled={formView.isSubmitting}
        css="flex-grow: 1; padding: 0px 4px;"
      />
      {formView.touched.username && formView.errors.username.length > 0 && (
        <WarningIcon size={16} color={cFA9400} />
      )}
    </Field>
  );
}

const Input = styled.input`
  color: ${cFFFFFF};
  font-size: 14px;
  font-weight: 500;
  border: none;
  background: none;
  padding: 0;

  &:disabled {
    color: ${rgba(cFFFFFF, 0.5)};
  }
`;

export function ToggleHideUsername() {
  const formView = useUsernameFormView();
  const formActions = useUsernameFormActions();
  const { t } = useTranslation();

  return (
    <CompactField
      label={t('Hide my Username')}
      labelId="username-form-hide-username-toggle"
    >
      <Toggle
        id="username-form-hide-username-toggle"
        checked={formView.values.isUsernamePrivate}
        onChange={formActions.toggleIsUsernamePrivate}
        disabled={formView.isSubmitting}
      />
    </CompactField>
  );
}

const CompactField = styled(Field).attrs({ mode: 'compact' })`
  justify-content: space-between;
  padding: 0 16px;
`;

export const Actions = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-flow: column;
  gap: 16px;
`;

interface SubmitProps {
  readonly className?: string;
}

export function Submit(props: SubmitProps) {
  const formView = useUsernameFormView();
  const { t } = useTranslation();
  return (
    <Button
      type="submit"
      disabled={!formView.canSubmit}
      className={props.className}
    >
      {t('Save')}
    </Button>
  );
}
